import React, { useState } from 'react';
import UploadedFileRow from './UploadedFileRow.jsx';
import FileDropzone from './FileDropzone.jsx';

import {
  deleteCourseProgram,
  getCourseProgramDownloadUrl,
  getCourseProgramViewUrl,
  postCourseProgram,
} from '../../../../models/course.js';

// Panel de "Gestionar PDF": muestra el archivo cargado (si existe),
// permite eliminarlo y subir/reemplazar uno nuevo.
//
// Props:
// - hasProgram: si el curso ya tiene un programa cargado.
// - fileName / fileMeta: datos del archivo actual (para UploadedFileRow).
const ManagePdfPanel = ({ courseId, hasProgram, fileName, fileMeta }) => {
  // Estado de UI (navegación del panel), no lógica de negocio.
  const [replacing, setReplacing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Estados de carga: bloquean el formulario para evitar dobles solicitudes.
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const busy = uploading || deleting;

  const startReplace = () => {
    setReplacing(true);
    setSelectedFile(null);
  };

  const cancelReplace = () => {
    if (busy) return;
    setReplacing(false);
    setSelectedFile(null);
  };

  const handleConfirmUpload = async () => {
    if (busy) return;
    if (!selectedFile) {
      window.alert('Se debe seleccionar un archivo primero');
      return;
    }
    if (selectedFile.type !== 'application/pdf') {
      window.alert('Solo se permiten archivos PDF.');
      return;
    }
    try {
      setUploading(true);
      const res = await postCourseProgram(courseId, selectedFile);
      if (res?.status >= 200 && res?.status < 300) {
        window.alert('Programa subido correctamente.');
        window.location.reload();
      } else {
        window.alert(
          res?.response?.data?.message || res?.response?.data || 'No se pudo subir el programa.'
        );
      }
    } catch (error) {
      // console.error(error);
      window.alert('Ocurrió un error al subir el programa.');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async () => {
    try {
      const { viewUrl } = await getCourseProgramViewUrl(courseId);
      window.open(viewUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      // console.error(error);
      window.alert('Ocurrió un error al obtener el enlace de visualización del programa.');
    }
  };

  const handleDownload = async () => {
    try {
      const { downloadUrl } = await getCourseProgramDownloadUrl(courseId);
      window.location.href = downloadUrl;
    } catch (error) {
      // console.error(error);
      window.alert('Ocurrió un error al descargar el programa.');
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    const confirmed = window.confirm('¿Eliminar el programa de este curso?');
    if (!confirmed) return;

    try {
      setDeleting(true);
      const res = await deleteCourseProgram(courseId);

      if (res?.status >= 200 && res?.status < 300) {
        window.alert('Programa eliminado correctamente.');
        window.location.reload();
      } else {
        window.alert(
          res?.response?.data?.message || res?.response?.data || 'No se pudo eliminar el programa.'
        );
      }
    } catch (error) {
      // console.error(error);
      window.alert('Ocurrió un error al eliminar el programa.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className='space-y-4'>
      {hasProgram && (
        <UploadedFileRow
          fileName={fileName}
          fileMeta={fileMeta}
          onView={handleView}
          onDownload={handleDownload}
          onDelete={handleDelete}
          busy={busy}
          deleting={deleting}
        />
      )}

      {replacing ? (
        <div className='space-y-3'>
          <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            {hasProgram ? 'Reemplazar archivo' : 'Subir archivo'}
          </p>
          <FileDropzone
            selectedFile={selectedFile}
            onFileSelect={setSelectedFile}
            placeholder='Selecciona un archivo PDF'
            disabled={busy}
          />
          <div className='flex gap-3'>
            <button
              type='button'
              onClick={cancelReplace}
              disabled={busy}
              className='flex-1 rounded-lg bg-muted px-4 py-2.5 font-semibold text-primary transition duration-150 hover:bg-muted-foreground/20 disabled:cursor-not-allowed disabled:opacity-50'>
              Cancelar
            </button>
            <button
              type='button'
              disabled={!selectedFile || busy}
              onClick={() => {
                handleConfirmUpload();
              }}
              className='flex-[2] rounded-lg bg-primary px-4 py-2.5 font-semibold text-white transition duration-150 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50'>
              {uploading ? 'Subiendo…' : 'Confirmar subida'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type='button'
          onClick={startReplace}
          disabled={busy}
          className='flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/25 px-4 py-4 font-medium text-muted-foreground transition duration-150 hover:border-primary/50 hover:bg-background disabled:cursor-not-allowed disabled:opacity-50'>
          {hasProgram ? '+ Subir nuevo archivo' : '+ Subir archivo'}
        </button>
      )}
    </div>
  );
};

export default ManagePdfPanel;
